import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Edit, Mail, Trash2, X } from 'lucide-react';
import { Tooltip } from '../../components/ui/Tooltip';
import { TrainingEmailModal } from '../../components/training/TrainingEmailModal';
import { usePractices } from '../../hooks/usePractices';
import { recipientGroups, sendPracticeEmail } from '../../utils/practiceEmail';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PermissionGate } from '../../components/PermissionGate';
import { CourseInfoCard } from '../../components/training/course/CourseInfoCard';
import { CourseTeachersCard } from '../../components/training/course/CourseTeachersCard';
import { CourseCostSummary } from '../../components/training/course/CourseCostSummary';
import { TeacherDetailModal } from '../../components/training/TeacherDetailModal';
import { CourseDeleteModal } from '../../components/training/CourseDeleteModal';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { courseStatusLabels } from '../../data/trainingCourseOptions';
import { formatCourseDate, getTrainedCourses, getTrainingProduct } from '../../utils/trainingCourses';
type SavedState = {
  saved?: 'created' | 'updated';
} | null;
export function CourseDashboardPage() {
  const {
    courseId
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const courses = useTrainingCourses();
  const course = courses.find(c => c.id === courseId);
  const [banner, setBanner] = useState((location.state as SavedState)?.saved);
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const {
    practices
  } = usePractices();
  useEffect(() => {
    setBanner((location.state as SavedState)?.saved);
  }, [location.key]);
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => setBanner(undefined), 4000);
    return () => clearTimeout(timer);
  }, [banner]);
  if (!course) return <Navigate to="/training/corsi" replace data-id="element-5948" data-mp="2mhnx1b6ktdzr" />;

  // Clients enrolled: positive practices linked to this course code
  const enrolled = practices.filter(p => p.status === 'Positivo' && p.courseCode === course.courseCode);
  const category = getTrainingProduct(course.productCode)?.category;
  const listHref = `/training/corsi?tab=${course.status}`;
  const period = course.startDate || course.endDate ? `${formatCourseDate(course.startDate) || '—'} → ${formatCourseDate(course.endDate) || '—'}` : '';
  return <Layout data-id="element-5949" data-mp="3f4s55p0yu8fr">
      <Link to={listHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-5950" data-mp="3f4iavp8w1f49">
        <ArrowLeft className="h-4 w-4" data-id="element-5951" data-mp="3p6vvnk6cycwh" /> Corsi
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6" data-id="element-5952" data-mp="3k8udr2ko4jml">
        <div className="min-w-0" data-id="element-5953" data-mp="3fbu3efgscupo">
          <p className="font-mono text-sm text-slate-500" data-id="element-5954" data-mp="3fb2j40sn2jep">{course.courseCode}</p>
          <h1 className="text-3xl font-bold text-seform-navy mt-1" data-id="element-5955" data-mp="3baijmqklf3pe">
            {course.name || 'Corso senza nome'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-5956" data-mp="bvws236w1gpr">
            <Badge variant="info" data-id="element-5957" data-mp="6viu5dz03n5g">{courseStatusLabels[course.status]}</Badge>
            <Badge variant="neutral" data-id="element-5958" data-mp="23hctdmz768k">{course.characteristic}</Badge>
            {category && <Badge variant="primary" data-id="element-5959" data-mp="3u8l5wxlramji">{category}</Badge>}
            {period && <span className="text-sm text-slate-500 ml-1 tabular-nums" data-id="element-5960" data-mp="ukjwqkl5k42t">
                {period}
              </span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0" data-id="element-5961" data-mp="psabviyw9u7e">
          <Tooltip content={enrolled.length > 0 ? `Invia a tutti i ${enrolled.length} clienti iscritti` : 'Nessun cliente iscritto a questo corso'} data-id="element-5962" data-mp="abaiyc89jj27">
            <Button variant="flat" color="secondary" startContent={<Mail className="h-4 w-4" data-id="element-5964" data-mp="5d93e1a1q1vh" />} disabled={enrolled.length === 0} onClick={() => setIsEmailing(true)} data-id="element-5963" data-mp="1cq81x8y2doj">
              Email ai partecipanti
              {enrolled.length > 0 && ` (${enrolled.length})`}
            </Button>
          </Tooltip>
          <PermissionGate module="training" action="delete" data-id="element-5965" data-mp="k987l0u1bhvc">
            <Button variant="flat" color="danger" startContent={<Trash2 className="h-4 w-4" data-id="element-5967" data-mp="1c1fpe4la10bb" />} onClick={() => setIsDeleting(true)} data-id="element-5966" data-mp="obd0tygkmchg">
              Elimina
            </Button>
          </PermissionGate>
          <PermissionGate module="training" action="edit" data-id="element-5968" data-mp="1gu0dcphi4j6x">
            <Button startContent={<Edit className="h-4 w-4" data-id="element-5970" data-mp="31g872fbidsgw" />} onClick={() => navigate(`/training/corsi/${course.id}/modifica`)} data-id="element-5969" data-mp="31h9nkzban760">
              Modifica
            </Button>
          </PermissionGate>
        </div>
      </div>

      <AnimatePresence data-id="element-5971" data-mp="369b796ask062">
        {banner && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" className="mb-6 flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3" data-id="element-5972" data-mp="369ji6sncsvem">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-5973" data-mp="2ni0e0ihlfbb0" />
            <p className="flex-1 text-sm font-medium text-green-700" data-id="element-5974" data-mp="7rdwt4ujitg9">
              {banner === 'created' ? 'Corso creato con successo' : 'Corso modificato con successo'}
            </p>
            <button type="button" aria-label="Chiudi messaggio" onClick={() => setBanner(undefined)} className="text-green-600 hover:text-green-800" data-id="element-5975" data-mp="9w0tnub0pzws">
              <X className="h-4 w-4" data-id="element-5976" data-mp="juqdvtqdzwg7" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-5977" data-mp="os9qkp7fs161">
        <div className="lg:col-span-2 space-y-6" data-id="element-5978" data-mp="3nb6ykybvcqij">
          <CourseInfoCard course={course} courses={courses} data-id="element-5979" data-mp="3n8koos1i9yx5" />
          <CourseTeachersCard teachers={course.teachers || []} onOpenTeacher={setTeacherId} data-id="element-5980" data-mp="3saa1od8agmnv" />
        </div>
        <aside className="lg:sticky lg:top-6" data-id="element-5981" data-mp="11cmfylvu85c">
          <CourseCostSummary course={course} data-id="element-5982" data-mp="5uz27znces5d" />
        </aside>
      </div>

      <TrainingEmailModal isOpen={isEmailing} title="Email ai partecipanti del corso" context={`${course.courseCode} · ${course.name || 'Corso senza nome'}`} groups={recipientGroups(enrolled)} onClose={() => setIsEmailing(false)} onSend={body => sendPracticeEmail(enrolled, body)} data-id="element-5983" data-mp="34k61kfub751m" />
      <TeacherDetailModal teacherId={teacherId} onClose={() => setTeacherId(null)} data-id="element-5984" data-mp="1mnm0yngrarks" />
      <CourseDeleteModal course={isDeleting ? course : null} trainedCount={getTrainedCourses(courses, course.id).length} onClose={() => setIsDeleting(false)} onConfirm={c => {
      navigate(listHref);
      trainingCoursesStore.remove(c.id);
    }} data-id="element-5985" data-mp="1hlmzxk8ho6r0" />
    </Layout>;
}